import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  SETTING_API_02,
  SETTING_API_08,
  SETTING_API_20,
  SETTING_API_21,
  ITEM_API_01,
} from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE, CON_STATUS_DELETED } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import { subCatergorydata } from "../../components/grid/GridData.tsx";
import {
  MainCategoryMessages,
  CategoryMessages,
  SubCategoryMessages,
  MessageCategory,
  Messages,
  UITextMessages,
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface MainCategory {
  maincatcode: string | number;
  maincatname: string;
}

interface Category {
  catcode: string | number;
  catname: string;
  maincatcode?: string | number;
}

interface SubCategory {
  subcatcode: string | number;
  subcatname: string;
  catcode?: string | number;
  maincatcode?: string | number;
  descrip?: string;
  [key: string]: unknown;
}

interface GenericName {
  gencode: number;
  genname: string;
  subcatcode: number;
  [key: string]: any;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    status?: number;
    data?: string | { message?: string; error?: string };
  };
  request?: unknown;
  message?: string;
}

interface DeletePayload {
  COMPCODE?: string;
  LOCCODE?: string;
  subcatcode: string | number;
  subcatname: string;
  descrip?: string;
  sts: string;
  enuser: string;
  MODE: string;
}

interface ApiResponse {
  success?: boolean;
  message?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface AutocompleteOption {
  label: string;
  value: string | number;
}

interface Column {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
}

interface RowWithId extends SubCategory {
  id: string | number;
}

function ViewSubcatergory(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();

  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  // ---------- STATE ----------
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState<boolean>(false);
  const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [data, setData] = useState<SubCategory[]>([]);
  const [filteredRows, setFilteredRows] = useState<SubCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [selectedItem, setSelectedItem] = useState<SubCategory | null>(null);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string | number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | number | null>(null);

  // ---------- Menu Items ----------
  const menuItems: MenuItem[] = [
    {
      text: SubCategoryMessages.action.edit,
      icon: <BorderColorOutlinedIcon />,
      onClick: (id: string | number) => handleItemClick(id, SubCategoryMessages.action.edit),
    },
    {
      text: SubCategoryMessages.action.delete,
      icon: <DeleteIcon />,
      onClick: (id: string | number) => handleItemClick(id, SubCategoryMessages.action.delete),
    },
  ];

  // ---------- Alerts ----------
  const showAlert = (message: string, type: MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING = MessageCategory.SUCCESS): void => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertOpen(true);
  };

  // ---------- Functions ----------
  const fetchMainCategories = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const result = await externalgetApi(SETTING_API_20, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });
      const list = Array.isArray(result) ? result : (result as any)?.data || [];
      setMainCategories(list);
      if (list.length > 0) setSelectedMainCategory(list[0].maincatcode);
    } catch (err) {
      console.error(err);
      showAlert(MainCategoryMessages.error.fetchFailed, MessageCategory.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async (): Promise<void> => {
    if (!selectedMainCategory) return;
    try {
      setIsLoading(true);
      const result = await externalgetApi(SETTING_API_02, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        MAINCATCODE: selectedMainCategory,
        STS: CON_STATUS_ACTIVE,
      });
      const list = Array.isArray(result) ? result : (result as any)?.data || [];
      setCategories(list);
      if (list.length > 0) setSelectedCategory(list[0].catcode);
    } catch (err) {
      console.error(err);
      showAlert(CategoryMessages.error.fetchCategoriesFailed, MessageCategory.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubcategories = async (): Promise<void> => {
    if (!selectedMainCategory || !selectedCategory) return;
    try {
      setIsLoading(true);
      const result = await externalgetApi(SETTING_API_08, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        MAINCATCODE: selectedMainCategory,
        CATCODE: selectedCategory,
        STS: CON_STATUS_ACTIVE,
      });
      const list = Array.isArray(result) ? result : (result as any)?.data || [];
      setData(list);
      setFilteredRows(list);
    } catch (err) {
      console.error(err);
      showAlert(SubCategoryMessages.error.fetchSubcategoriesFailed, MessageCategory.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to check if subcategory is used in any generic names
  const checkSubcategoryInGenericNames = async (subcatcode: string | number): Promise<boolean> => {
    try {
      setIsCheckingUsage(true);
      
      // Fetch generic names
      const genericResponse = await externalgetApi(SETTING_API_21, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      });

      const genericList = Array.isArray(genericResponse)
        ? genericResponse
        : (genericResponse as any)?.data
          ? (genericResponse as any).data
          : [];

      // Check if any generic names use this subcategory
      const isUsed = (genericList as GenericName[]).some(
        (g: GenericName) => Number(g.subcatcode) === Number(subcatcode)
      );

      return isUsed;
    } catch (error) {
      console.error("Error checking subcategory usage in generic names:", error);
      return false;
    } finally {
      setIsCheckingUsage(false);
    }
  };

  // ---------- useEffect ----------
  useEffect(() => {
    setPageData({ title: SubCategoryMessages.pageTitle.view } as PageData);
  }, [setPageData]);

  useEffect(() => {
    fetchMainCategories();
  }, [COMPCODE, STAFFMAPLOCODE]);

  useEffect(() => {
    fetchCategories();
  }, [selectedMainCategory]);

  useEffect(() => {
    fetchSubcategories();
  }, [selectedMainCategory, selectedCategory]);

  // ---------- Search ----------
  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);
    
    if (!searchValue.trim()) {
      setFilteredRows(data || []);
      return;
    }

    const q = searchValue.toLowerCase().trim();
    
    const filtered = data?.filter((row) => {
      const subcatcodeStr = row.subcatcode?.toString() || "";
      
      return (
        row.subcatname?.toLowerCase().includes(q) ||
        subcatcodeStr.toLowerCase().includes(q)
      );
    });
    
    setFilteredRows(filtered || []);
  };

  // ---------- Action Buttons ----------
  const handleItemClick = (id: string | number, actionText: string): void => {
    const item = data.find((row) => row.subcatcode === id);
    if (!item) return;

    if (actionText === SubCategoryMessages.action.edit) {
      navigate("/CreateSubCategory", {
        state: { selectedItem: item, actionText },
      });
    }
    if (actionText === SubCategoryMessages.action.delete) {
      setSelectedId(id);
      setSelectedItem(item);
      setOpenDeleteDialog(true); // Open confirmation dialog immediately
    }
  };

  // ---------- Add ----------
  const addToView = (): void => {
    navigate("/CreateSubCategory", {
      state: {
        selectedMainCategory,
        selectedCategory,
        existingSubCategories: data,
      },
    });
  };

  // ---------- Delete ----------
  const confirmDelete = async (): Promise<void> => {
    if (!selectedId || !selectedItem) return;
    
    try {
      setIsLoading(true);
      
      // Check if subcategory is used in any generic names
      const isUsed = await checkSubcategoryInGenericNames(selectedId);
      
      // If subcategory is in use, show error message and don't delete
      if (isUsed) {
        showAlert(
          "Subcategory cannot be deleted. It's already in use",
          MessageCategory.ERROR
        );
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
        return;
      }

      const payload: DeletePayload = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        subcatcode: selectedId,
        subcatname: selectedItem.subcatname,
        descrip: selectedItem.descrip,
        sts: CON_STATUS_DELETED || "D",
        enuser: USERNAME || "Admin",
        MODE: "DELETE",
      };

      console.log("DELETE payload:", payload);
      console.log("DELETE URL:", SETTING_API_08);

      const response = (await externalpostApi(SETTING_API_08, payload)) as ApiResponse;
      
      console.log("DELETE response:", response);
      
      if (response && response.success !== false) {
        setData((prev) => prev.filter((row) => row.subcatcode !== selectedId));
        setFilteredRows((prev) =>
          prev.filter((row) => row.subcatcode !== selectedId)
        );
        showAlert(
          SubCategoryMessages.success.delete,
          MessageCategory.SUCCESS
        );
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
      } else {
        throw new Error(response?.message || SubCategoryMessages.error.deleteFailed);
      }
    } catch (err) {
      const error = err as ApiError;
      console.error("DELETE API error:", err);

      // Error handling
      let errorMessage = SubCategoryMessages.error.deleteFailed;

      if (error.response) {
        console.error("Response Status:", error.response.status);
        console.error("Response Data:", error.response.data);

        if (error.response.data) {
          if (typeof error.response.data === "string") {
            if (error.response.data.includes("Object reference not set to an instance of an object")) {
              errorMessage = "Null reference error occurred";
            } else if (error.response.data.trim() !== "") {
              errorMessage = error.response.data;
            }
          } else if ((error.response.data as any).message) {
            errorMessage = (error.response.data as any).message;
          } else if ((error.response.data as any).error) {
            errorMessage = (error.response.data as any).error;
          }
        }

        // Handle specific HTTP status codes
        switch (error.response.status) {
          case 400:
            errorMessage = errorMessage || "Bad request";
            break;
          case 404:
            errorMessage = "Resource not found";
            break;
          case 405:
            errorMessage = "Method not allowed";
            break;
          case 500:
            errorMessage = errorMessage || "Server error";
            break;
          default:
            errorMessage = `${errorMessage} (Status: ${error.response.status})`;
        }
      } else if (error.request) {
        errorMessage = "No response from server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      showAlert(errorMessage, MessageCategory.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Cancel Delete ----------
  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  };

  // Helper functions for Autocomplete
  const getMainCategoryOptions = (): AutocompleteOption[] => {
    return mainCategories.map((mc) => ({
      label: mc.maincatname,
      value: mc.maincatcode,
    }));
  };

  const getCategoryOptions = (): AutocompleteOption[] => {
    return categories.map((cat) => ({
      label: cat.catname,
      value: cat.catcode,
    }));
  };

  const findSelectedMainCategory = (): AutocompleteOption | undefined => {
    const options = getMainCategoryOptions();
    return options.find((opt) => opt.value === selectedMainCategory);
  };

  const findSelectedCategory = (): AutocompleteOption | undefined => {
    const options = getCategoryOptions();
    return options.find((opt) => opt.value === selectedCategory);
  };

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    id: row.subcatcode || Math.random(),
  }));

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UITextMessages.breadcrumb.home}
          </Link>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {UITextMessages.breadcrumb.settings}
          </Link>
          <Link
            underline="hover"
            sx={styles.breadcrumb_link_page}
            aria-current="page"
          >
            {SubCategoryMessages.breadcrumb.view}
          </Link>
        </Breadcrumbs>
      </Box>

      {/* ---------- AppBar ---------- */}
      <Box>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }} sx={{ flex: 1 }}>
              <Typography sx={styles.appbar_title}>
                {subCatergorydata.name}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
              {/* ---------- Main Category ----------*/}
              <Autocomplete
                id="act_maincategory"
                options={getMainCategoryOptions()}
                getOptionLabel={(option: AutocompleteOption) => option.label || ""}
                disableClearable={true}
                value={findSelectedMainCategory() || null}
                onChange={(_, newValue: AutocompleteOption | null) => {
                  setSelectedMainCategory(newValue ? newValue.value : null);
                  setSelectedCategory(null);
                  setCategories([]);
                  setData([]);
                  setFilteredRows([]);
                }}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={SubCategoryMessages.label.mainCategory}
                    size="small"
                    fullWidth
                  />
                )}
              />

              {/* ---------- Category ----------*/}
              <Autocomplete
                id="act_category"
                options={getCategoryOptions()}
                getOptionLabel={(option: AutocompleteOption) => option.label || ""}
                disableClearable={true}
                value={findSelectedCategory() || null}
                onChange={(_, newValue: AutocompleteOption | null) => {
                  setSelectedCategory(newValue ? newValue.value : null);
                  setData([]);
                  setFilteredRows([]);
                }}
                sx={{ minWidth: 200 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={SubCategoryMessages.label.category}
                    size="small"
                    fullWidth
                  />
                )}
              />

              {/* ---------- Search ---------- */}
              <Autocomplete
                id="act_appbarsearch"
                freeSolo
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={{ ...styles.ipt_appbar_search_auto, minWidth: 200 }}
                options={data?.map((row) => row.subcatname).filter(Boolean) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder={SubCategoryMessages.placeholder.search}
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              id="btn_appbarsearch"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />

              {/* ---------- Add Button ----------*/}
              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
                disabled={isLoading}
              >
                {SubCategoryMessages.button.add}
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        {/* ---------- Grid ---------- */}
        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={subCatergorydata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={subCatergorydata.name}
            gridid="subcatcode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      {/* ---------- Delete Dialog ---------- */}
      <Dialog
        id="dlg_delete"
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">{SubCategoryMessages.dialog.deleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedItem ? 
              `Are you sure you want to delete the subcategory "${selectedItem.subcatname}"?` : 
              SubCategoryMessages.dialog.deleteConfirmation}
          </DialogContentText>
          {/* {selectedItem?.descrip && (
            <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
              Description: {selectedItem.descrip}
            </DialogContentText>
          )} */}
        </DialogContent>
        <DialogActions>
          <Button
            id="btn_cancel"
            onClick={cancelDelete}
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            disabled={isLoading || isCheckingUsage}
          >
            {UITextMessages.button.cancel}
          </Button>
          <Button
            id="btn_confirmdelete"
            onClick={confirmDelete}
            color="error"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            variant="contained"
            disabled={isLoading || isCheckingUsage}
          >
            {isLoading ? "Deleting..." : UITextMessages.button.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewSubcatergory;